import { Box, Button, Paper, TextField } from '@mui/material'
import { action, runInAction } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import { useCallback, useEffect, useMemo } from 'react'
import { useParams } from 'react-router'
import {
	BaseWorkflowFailureActionInstance,
	WorkflowFailureActionConfiguration,
	WorkflowFailureActionInstance,
} from '../../../../../api/clients/workflows/DTOs'
import WorkflowFailureActionInstancesClient from '../../../../../api/clients/workflows/WorkflowFailureActionInstancesClient'
import WorkflowFailureActionsClient from '../../../../../api/clients/workflows/WorkflowFailureActionsClient'
import { FullscreenSpinner } from '../../../../../components/feedback/circular'
import {
	FormBuilderViewer,
	StandaloneCellManager,
} from '../../../../../modules/FormBuilderInterop/Runtimes/Standalone'
import { toastService } from '../../../../../services/notifications/ToastService'
import { compareState } from '../../../../../utils/compareState'
import {
	FixedHeader,
	FixedHeaderWithHint,
} from '../../../../../utils/HOC/FixedHeaders'
import AdministrationPageContainer from '../../AdministrationPageContainer'
import { updateBreadcrumbs } from '../../Breadcrumbs'

export const WorkflowFailureActionPage = observer(() => {
	const params = useParams<{ workflowId: string; instanceId: string }>()

	const instanceId = parseInt(params.instanceId)
	const workflowId = parseInt(params.workflowId)

	if (isNaN(instanceId)) throw new Error('invalid failure action instance id')
	if (isNaN(workflowId)) throw new Error('invalid workflow id')

	updateBreadcrumbs(
		`/_administration/_workflows/${workflowId}/failure-actions`,
		'Failure Actions',
	)

	const localStore = useLocalObservable(() => ({
		instance: undefined as WorkflowFailureActionInstance | undefined,
		authenticationProvider: undefined as
			| WorkflowFailureActionConfiguration
			| undefined,
		displayName: '',
		description: '',
		instanceState: {} as Record<string, unknown>,
		normalizedInstanceNames: [] as string[],
	}))

	const failureActionsClient = new WorkflowFailureActionsClient()
	const instancesClient = new WorkflowFailureActionInstancesClient()

	useEffect(() => {
		fetchInstance()
		getInstanceNames()
	}, [instanceId])

	const fetchInstance = async () => {
		const { data: instance } = await instancesClient.getInstance(instanceId)

		const { data: failureAction } = await failureActionsClient.getFailureAction(
			instance.failureActionId,
		)

		runInAction(() => {
			localStore.instance = instance
			localStore.displayName = instance.displayName
			localStore.description = instance.description
			localStore.authenticationProvider = failureAction
			localStore.instanceState = instance.state as Record<string, unknown>
		})
	}

	const getInstanceNames = async () => {
		const { data: instances } = await instancesClient.getMetadataForInstances(
			workflowId,
		)

		runInAction(() => {
			localStore.normalizedInstanceNames = instances
				.filter((v) => v.id !== instanceId)
				.map((v) => v.displayName.toUpperCase().trim())
		})
	}

	const nameIsDuplicate = useCallback(
		() =>
			localStore.normalizedInstanceNames.includes(
				localStore.displayName.toUpperCase().trim(),
			),
		[localStore.normalizedInstanceNames, instanceId, localStore.displayName],
	)

	const handleUpdateInstance = async () => {
		if (localStore.instance === undefined) return

		if (nameIsDuplicate()) {
			toastService.displayToast({
				message:
					'A failure action with this name already exists in this workflow',
				area: 'global',
				severity: 'error',
			})
		}

		const baseModel: BaseWorkflowFailureActionInstance = {
			failureActionId: localStore.instance.failureActionId,
			displayName: localStore.displayName,
			description: localStore.description,
			state: localStore.instanceState,
			workflowId: localStore.instance.workflowId,
		}

		try {
			const { data: updatedInstance } = await instancesClient.updateInstance(
				instanceId,
				baseModel,
			)

			runInAction(() => {
				localStore.instance = updatedInstance
				localStore.displayName = updatedInstance.displayName
				localStore.description = updatedInstance.description
				localStore.instanceState = updatedInstance.state
			})

			toastService.displayToast({
				message: 'Failure action updated',
				area: 'global',
				severity: 'success',
			})
		} catch (error) {
			console.log(error)

			toastService.displayToast({
				message: 'Error updating failure action',
				area: 'global',
				severity: 'error',
			})
		}
	}

	const cellManager = useMemo(() => {
		if (
			localStore.instance === undefined ||
			localStore.authenticationProvider === undefined
		)
			return undefined

		return new StandaloneCellManager(
			localStore.authenticationProvider.formBuilderSchema,
			localStore.instanceState,
		)
	}, [
		localStore.instance,
		localStore.instanceState,
		localStore.authenticationProvider,
	])

	const isDisabled = (): boolean => {
		// if no values have changed we want the button to be disabled
		if (
			localStore.instance === undefined ||
			localStore.displayName === '' ||
			nameIsDuplicate()
		)
			return true

		if (
			!compareState(
				localStore.instance.state as Record<string, unknown>,
				localStore.instanceState,
			)
		)
			return false

		return (
			localStore.displayName === localStore.instance?.displayName &&
			localStore.description === localStore.instance?.description
		)
	}

	if (localStore.instance === undefined) return <FullscreenSpinner />

	return (
		<AdministrationPageContainer title={localStore.instance.displayName}>
			<Box display="flex" flexDirection="column" height="100%" width="100%">
				<Box
					component={Paper}
					margin={(theme) => theme.spacing(2, 0)}
					padding={(theme) => theme.spacing(1, 2)}
					width="100%"
				>
					<Box display="flex" flexDirection="column" gap={2}>
						<FixedHeaderWithHint
							label="Display Name"
							hint={
								(nameIsDuplicate() &&
									'A failure action with this name already exists in this workflow') ||
								(localStore.displayName === '' && 'Name is required')
							}
							hintColor="error.main"
						>
							<TextField
								value={localStore.displayName}
								size="small"
								fullWidth
								onChange={action(
									(evt) => (localStore.displayName = evt.target.value),
								)}
							/>
						</FixedHeaderWithHint>
						<FixedHeader label="Description">
							<TextField
								value={localStore.description}
								size="small"
								fullWidth
								onChange={action(
									(evt) => (localStore.description = evt.target.value),
								)}
							/>
						</FixedHeader>{' '}
						{cellManager !== undefined && (
							<FormBuilderViewer cellManager={cellManager} />
						)}
					</Box>
				</Box>
				<Box alignSelf={'flex-end'} paddingBottom={3}>
					<Button
						color="primary"
						variant="contained"
						onClick={handleUpdateInstance}
						disabled={isDisabled()}
					>
						Save Changes
					</Button>
				</Box>
			</Box>
		</AdministrationPageContainer>
	)
})
